import { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { auth, db } from '../firebase-config.js';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  increment,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';

const ActivityContext = createContext(null);

const ACTIVITY_LS_KEY = 'activityLog';
const ACTIVITY_MIGRATED_FLAG = 'activityFirestoreMigrated';

// ── Helpers ─────────────────────────────────────────────────────────────────

function toISOParis(date = new Date()) {
  const f = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const [dd, mm, yyyy] = f.format(date).split('/');
  return `${yyyy}-${mm}-${dd}`;
}

function toMillis(value) {
  if (typeof value === 'number') return value;
  if (value?.toMillis) return value.toMillis();
  if (value instanceof Date) return value.getTime();
  return null;
}

function makeId(prefix = 'entry') {
  return crypto.randomUUID?.()
    ?? `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function normalizeEntry(raw = {}) {
  const type = raw.type ?? 'listen';
  const createdAt = toMillis(raw.createdAt) ?? Date.now();
  const updatedAt = toMillis(raw.updatedAt) ?? createdAt;

  return {
    id: raw.id ?? makeId(type),
    dateISO: raw.dateISO ?? toISOParis(new Date(createdAt)),
    type,
    minutes: Number(raw.minutes) || 0,
    content: typeof raw.content === 'string' ? raw.content : '',
    source: raw.source ?? 'activityLog',
    meta: raw.meta ?? null,
    createdAt,
    updatedAt,
  };
}

function serializeEntryForFirestore(entry) {
  const e = normalizeEntry(entry);
  return {
    ...e,
    createdAt: new Date(e.createdAt),
    updatedAt: new Date(e.updatedAt),
  };
}

function fromFirestoreDoc(docSnap) {
  const data = docSnap.data() || {};
  return normalizeEntry({
    id: docSnap.id,
    ...data,
    createdAt: data.createdAt?.toMillis?.() ?? data.createdAt,
    updatedAt: data.updatedAt?.toMillis?.() ?? data.updatedAt,
  });
}

function loadFromStorage(key) {
  if (!key) return [];
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map(normalizeEntry) : [];
  } catch (e) {
    console.warn('❌ Erreur lecture activityLog:', e);
    return [];
  }
}

function saveToStorage(key, entries) {
  if (!key) return;
  try {
    const normalized = Array.isArray(entries) ? entries.map(normalizeEntry) : [];
    localStorage.setItem(key, JSON.stringify(normalized));
  } catch (e) {
    console.warn('❌ Erreur écriture activityLog:', e);
  }
}

function parseFrDateToISO(frStr) {
  const FR_MONTHS = [
    'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
  ];

  try {
    const [datePart] = frStr.split(',').map((s) => s.trim());
    const [dd, mois, yyyy] = datePart.split(' ');
    const monthIndex = FR_MONTHS.indexOf(mois.toLowerCase());
    if (monthIndex === -1) return null;

    const d = new Date(Number(yyyy), monthIndex, Number(dd));
    if (Number.isNaN(d.getTime())) return null;

    return toISOParis(d);
  } catch {
    return null;
  }
}

function migrateRessentisIfNeeded(uid, currentActivity) {
  const oldKey = `ressentis:${uid}`;
  const migrationFlag = `ressentis_migrated:${uid}`;

  if (localStorage.getItem(migrationFlag)) return currentActivity;

  try {
    const rawRessentis = localStorage.getItem(oldKey);
    if (!rawRessentis) {
      localStorage.setItem(migrationFlag, 'true');
      return currentActivity;
    }

    const ressentis = JSON.parse(rawRessentis);

    const newEntries = (Array.isArray(ressentis) ? ressentis : [])
      .map((r) => ({
        id: makeId('ressenti'),
        dateISO: parseFrDateToISO(r.date),
        type: 'ressenti',
        minutes: 0,
        content: r.content || '',
        source: 'ressentis',
        createdAt: Date.now(),
        updatedAt: Date.now(),
      }))
      .filter((e) => e.dateISO);

    localStorage.setItem(migrationFlag, 'true');
    localStorage.removeItem(oldKey);

    console.log(`✅ Migration ressentis legacy: ${newEntries.length} entrée(s)`);

    return [...currentActivity, ...newEntries].map(normalizeEntry);
  } catch (e) {
    console.warn('⚠️ Erreur migration ressentis:', e);
    localStorage.setItem(migrationFlag, 'true');
    return currentActivity;
  }
}

async function batchSetEntries(uid, entries) {
  if (!uid || uid === 'guest' || !entries.length) return;

  const normalized = entries.map(normalizeEntry);
  const chunkSize = 400;

  for (let i = 0; i < normalized.length; i += chunkSize) {
    const chunk = normalized.slice(i, i + chunkSize);
    const batch = writeBatch(db);

    chunk.forEach((entry) => {
      const ref = doc(db, 'users', uid, 'activity', entry.id);
      batch.set(ref, serializeEntryForFirestore(entry), { merge: true });
    });

    await batch.commit();
  }
}

async function replaceFirestoreActivity(uid, entries) {
  if (!uid || uid === 'guest') return;

  const colRef = collection(db, 'users', uid, 'activity');
  const snap = await getDocs(colRef);
  const normalized = Array.isArray(entries) ? entries.map(normalizeEntry) : [];

  const ops = [
    ...snap.docs.map((d) => ({ type: 'delete', ref: d.ref })),
    ...normalized.map((entry) => ({
      type: 'set',
      ref: doc(db, 'users', uid, 'activity', entry.id),
      data: serializeEntryForFirestore(entry),
    })),
  ];

  const chunkSize = 400;

  for (let i = 0; i < ops.length; i += chunkSize) {
    const chunk = ops.slice(i, i + chunkSize);
    const batch = writeBatch(db);

    chunk.forEach((op) => {
      if (op.type === 'delete') batch.delete(op.ref);
      if (op.type === 'set') batch.set(op.ref, op.data, { merge: true });
    });

    await batch.commit();
  }
}

// ── Provider ────────────────────────────────────────────────────────────────

export function ActivityProvider({ children }) {
  const [uid, setUid] = useState(null);
  const [activity, setActivity] = useState([]);
  const [activityUid, setActivityUid] = useState(null);
  const [isHydrating, setIsHydrating] = useState(true);

  // 1) Auth = source de vérité
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setUid(user?.uid ?? 'guest');
    });

    return () => unsub();
  }, []);

  // 2) Chargement + migration + abonnement Firestore
  useEffect(() => {
    if (!uid) return;

    let cancelled = false;
    let unsubSnapshot = () => {};

    const key = `${ACTIVITY_LS_KEY}:${uid}`;

    const boot = async () => {
      setIsHydrating(true);

      // Cache local immédiat
      let localActivity = loadFromStorage(key);
      localActivity = migrateRessentisIfNeeded(uid, localActivity);
      saveToStorage(key, localActivity);

      if (!cancelled) {
        setActivityUid(uid);
        setActivity(localActivity);
      }

      // Utilisateur non connecté => local uniquement
      if (uid === 'guest') {
        if (!cancelled) setIsHydrating(false);
        return;
      }

      try {
        const migrationFlag = `${ACTIVITY_MIGRATED_FLAG}:${uid}`;

        // Migration one-shot du cache local vers Firestore
        if (localActivity.length > 0 && localStorage.getItem(migrationFlag) !== 'true') {
          await batchSetEntries(uid, localActivity);
          localStorage.setItem(migrationFlag, 'true');
          console.log('✅ Migration activityLog local -> Firestore terminée');
        }

        if (cancelled) return;

        const q = query(
          collection(db, 'users', uid, 'activity'),
          orderBy('createdAt', 'asc')
        );

        unsubSnapshot = onSnapshot(
          q,
          (snapshot) => {
            const remoteActivity = snapshot.docs.map(fromFirestoreDoc);
            setActivityUid(uid);
            setActivity(remoteActivity);
            setIsHydrating(false);
          },
          (error) => {
            console.error('❌ Erreur sync activité Firestore:', error);
            setActivityUid(uid);
            setActivity(localActivity);
            setIsHydrating(false);
          }
        );
      } catch (error) {
        console.error('❌ Erreur migration Firestore:', error);
        if (!cancelled) {
          setActivityUid(uid);
          setActivity(localActivity);
          setIsHydrating(false);
        }
      }
    };

    boot();

    return () => {
      cancelled = true;
      unsubSnapshot();
    };
  }, [uid]);

// 3) Cache local miroir
useEffect(() => {
  if (!uid || activityUid !== uid) return;

  saveToStorage(`${ACTIVITY_LS_KEY}:${uid}`, activity);
}, [activity, uid, activityUid]);

// 4) Sync guest / cache entre onglets
useEffect(() => {
  if (!uid) return;

  const key = `${ACTIVITY_LS_KEY}:${uid}`;

  const handleStorage = (e) => {
    if (uid !== 'guest') return;

    // On ignore les events artificiels ou les autres clés localStorage
    if (!e.key || e.key !== key) return;

    const nextActivity = loadFromStorage(key);

    setActivityUid(uid);
    setActivity((prev) => {
      const same =
        JSON.stringify(prev) === JSON.stringify(nextActivity);

      return same ? prev : nextActivity;
    });
  };

  window.addEventListener('storage', handleStorage);
  return () => window.removeEventListener('storage', handleStorage);
}, [uid]);

  // ── Mutations ────────────────────────────────────────────────────────────

  const addEntry = useCallback(async (entry) => {
    if (!uid) return null;

    const normalized = normalizeEntry({
      ...entry,
      createdAt: entry?.createdAt ?? Date.now(),
      updatedAt: Date.now(),
    });

    // Guest = local only
    if (uid === 'guest') {
      setActivity((prev) => {
        if (normalized.type === 'listen') {
          const exists = prev.find(
            (e) =>
              e.dateISO === normalized.dateISO &&
              e.type === 'listen' &&
              e.meta?.trackId === normalized.meta?.trackId
          );

          if (exists) {
            return prev.map((e) =>
              e.id === exists.id
                ? {
                    ...e,
                    minutes: (e.minutes || 0) + (normalized.minutes || 0),
                    updatedAt: Date.now(),
                  }
                : e
            );
          }
        }

        return [...prev, normalized];
      });

      return normalized.id;
    }

    // Connecté = Firestore source de vérité
    if (normalized.type === 'listen') {
      const exists = activity.find(
        (e) =>
          e.dateISO === normalized.dateISO &&
          e.type === 'listen' &&
          e.meta?.trackId === normalized.meta?.trackId
      );

      if (exists) {
        await updateDoc(doc(db, 'users', uid, 'activity', exists.id), {
          minutes: increment(normalized.minutes || 0),
          updatedAt: new Date(),
        });

        return exists.id;
      }
    }

    await setDoc(
      doc(db, 'users', uid, 'activity', normalized.id),
      serializeEntryForFirestore(normalized),
      { merge: true }
    );

    return normalized.id;
  }, [uid, activity]);

  const deleteEntry = useCallback(async (id) => {
    if (!uid || !id) return;

    if (uid === 'guest') {
      setActivity((prev) => prev.filter((e) => e.id !== id));
      return;
    }

    await deleteDoc(doc(db, 'users', uid, 'activity', id));
  }, [uid]);

  const replaceAll = useCallback(async (entries) => {
    const normalized = Array.isArray(entries) ? entries.map(normalizeEntry) : [];

    if (!uid) return;

    if (uid === 'guest') {
      setActivity(normalized);
      return;
    }

    await replaceFirestoreActivity(uid, normalized);
  }, [uid]);

  const value = useMemo(
    () => ({
      activity,
      log: activity,
      addEntry,
      deleteEntry,
      replaceAll,
      isHydrating,
      isCloudBacked: uid !== 'guest',
    }),
    [activity, addEntry, deleteEntry, replaceAll, isHydrating, uid]
  );

  return (
    <ActivityContext.Provider value={value}>
      {children}
    </ActivityContext.Provider>
  );
}

export function useActivity() {
  const ctx = useContext(ActivityContext);

  if (!ctx) {
    console.warn('⚠️ useActivity utilisé hors ActivityProvider');
    return {
      activity: [],
      log: [],
      addEntry: async () => null,
      deleteEntry: async () => {},
      replaceAll: async () => {},
      isHydrating: false,
      isCloudBacked: false,
    };
  }

  return ctx;
}